// ============================================================
// AnalyticsPlugin — Jitsu-based monitoring
// Tracks HLS errors, buffer issues, viewing params, speed
//
// Anti-spam rules
//   • buffer:stall   → only when isPlaying && currentTime > 30 s
//   • hls:error (networkError, non-fatal) → only when stream isOnline
//   • hls:error (fatal networkError) → only when stream isOnline
//   • hls:speed:report → only when stream isOnline
//   • buffer:low       → throttled: max 1 per BUFFER_LOW_THROTTLE ms
//   • buffer:critical  → throttled: max 1 per BUFFER_CRITICAL_THROTTLE ms
//   • buffer:append:error → throttled: max 1 per BUFFER_APPEND_ERROR_THROTTLE ms
// ============================================================
import type { IPlugin, PluginContext, PlayerError, VideoStateMetrics } from '../core/types';
import { QoeTelemetry } from './QoeTelemetry';
import { buildVideoStateMetrics } from '../engine/BaseHlsEngine/videoStateMetrics';
import { version as playerVersion } from '../../package.json';

export interface AnalyticsPluginOptions {
  /** Jitsu script URL (default: https://vmetrics.goodgame.ru:8081/p.js) */
  endpoint?: string;
  /** Jitsu write key */
  writeKey?: string;
  /** Debug mode */
  debug?: boolean;
}

const DEFAULT_ENDPOINT = 'https://vmetrics.goodgame.ru:8081/p.js';
const DEFAULT_WRITE_KEY = 'poxZRQI1rxpy6MaqhZ4tQefpXAvu5H61:Xhovt10VStvUCiLzECzdjM3m9dOuIdDm';

/** Min ms between consecutive buffer:low analytics events */
const BUFFER_LOW_THROTTLE = 30_000;
/** Min ms between consecutive buffer:critical analytics events */
const BUFFER_CRITICAL_THROTTLE = 60_000;
/** Min ms between consecutive buffer:append:error analytics events */
const BUFFER_APPEND_ERROR_THROTTLE = 60_000;
/** Periodic latency gauge interval for LL-HLS (ms) */
const LATENCY_GAUGE_INTERVAL = 120_000;
const MIN_LAG_DURATION_MS = 1000;
// Match exposure accounting: a suspended browser is not an observed stall.
const MAX_LAG_OBSERVATION_GAP_MS = 5000;
const ERROR_CONTEXT_WINDOW_MS = 30_000;

type LagError = { at: number; type: string; details: string; responseCode?: number | null; fatal: boolean };
type LagEpisode = {
  id: string;
  reported: boolean;
  startedAt: number;
  observedDurationMs: number;
  unobservedDurationMs: number;
  currentTime: number;
  context: Record<string, unknown>;
  signals: Set<string>;
  error?: LagError;
};

export class AnalyticsPlugin implements IPlugin {
  readonly name = 'analytics';

  private ctx: PluginContext | null = null;
  private jitsu: unknown = null;
  private unsubs: Array<() => void> = [];
  private anonymousId: string;
  private latencyTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private qoe: QoeTelemetry | null = null;
  private sessionId = '';
  private sequence = 0;
  private lagUpdateTimer: ReturnType<typeof setInterval> | null = null;
  private pendingEvents: Array<{ name: string; payload: unknown }> = [];
  private userId: number | undefined = undefined;

  // Anti-spam timestamps
  private lastBufferLowAt = 0;
  private lastBufferCriticalAt = 0;
  private lastBufferAppendErrorAt = 0;
  private playbackObserved = false;
  private playbackAdvanced = false;
  private lastPlaybackTime = 0;
  private lastPlaybackAt = 0;
  private lastPlaybackProgressAt = 0;
  private playlistUnavailable = false;
  private lag: LagEpisode | null = null;
  private lagTimer: ReturnType<typeof setTimeout> | null = null;
  private lagSequence = 0;
  private recentLagError: LagError | undefined;

  constructor(private options: AnalyticsPluginOptions = {}) {
    this.anonymousId = this.getOrCreateAnonymousId();
  }

  init(ctx: PluginContext): void {
    this.ctx = ctx;
    this.sessionId = globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`;
    this.sequence = 0;
    if (ctx.config.apiData?.user?.user_id) {
      this.userId = ctx.config.apiData.user.user_id;
    }
    this.loadJitsu();
    this.bindEvents();
    this.qoe = new QoeTelemetry(ctx, (name, data) => this.trackEvent(name, data), () =>
      this.lag?.reported ? { lagId: this.lag.id, durationMs: Date.now() - this.lag.startedAt } : undefined,
      (time, elapsedMs, mode, at) => {
        const video = this.ctx?.getEngine()?.getVideoElement?.();
        if (video?.paused || mode === 'paused') this.resetLag('paused');
        else if (video?.seeking || mode === 'seeking') this.resetLag('seeking');
        else if (mode === 'offline') this.resetLag('offline');
        else if (video && mode !== 'unobserved') this.observePlaybackProgress(time);
        const episode = this.lag;
        if (!episode) return;
        const observedMs = Math.max(0, at - Math.max(episode.startedAt, at - elapsedMs));
        if (mode === 'buffering') episode.observedDurationMs += observedMs;
        if (mode === 'unobserved') episode.unobservedDurationMs += observedMs;
      });
    this.lagUpdateTimer = setInterval(() => {
      if (!this.lag?.reported || !this.isStreamOnline()) return;
      const video = this.ctx?.getEngine()?.getVideoElement?.();
      const snapshot = video ? this.flattenVideoMetrics(buildVideoStateMetrics(video, 'waiting',
        typeof this.lag.context.src === 'string' ? this.lag.context.src : undefined,
        this.ctx?.store.getState().quality)) : {};
      this.trackEvent('hls_playback_lag_update', { ...this.lagPayload(this.lag),
        ...this.getViewingParams(), ...snapshot, videoSnapshotAt: video ? Date.now() : undefined,
        phase: 'updated', isLag: true, recovered: false, updateKind: 'heartbeat' });
    }, 10_000);
    this.startLatencyGauge();
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = setInterval(() => {
      const state = this.ctx?.store.getState();
      if (!state?.isOnline || !state.isPlaying) return;
      this.trackEvent(state.engineName === 'ws' ? 'ws_viewing_params' : 'hls_viewing_params', this.getViewingParams());
    }, 60_000);
    ctx.logger.debug('AnalyticsPlugin initialized');
  }

  destroy(): void {
    this.qoe?.destroy(); this.qoe = null;
    if (this.lagUpdateTimer) clearInterval(this.lagUpdateTimer);
    this.lagUpdateTimer = null;
    this.resetLag('destroyed');
    this.unsubs.forEach(fn => fn());
    this.unsubs = [];
    this.stopLatencyGauge();
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
    this.ctx = null;
    this.userId = undefined;
  }

  /** Track a custom event */
  track(eventName: string, data?: Record<string, unknown>): void {
    this.trackEvent(eventName, data ?? {});
  }

  // --- Private ---

  private loadJitsu(): void {
    const callbackName = 'playerJitsuOnload';
    const namespaceName = 'playerJitsu';

    const win = window as unknown as Record<string, unknown>;
    win[callbackName] = (j: unknown) => {
      this.jitsu = j;
      this.flushPendingEvents(j);
    };

    if (win[namespaceName]) {
      this.jitsu = win[namespaceName];
      return;
    }

    const script = document.createElement('script');
    script.src = this.options.endpoint ?? DEFAULT_ENDPOINT;
    script.setAttribute('data-write-key', this.options.writeKey ?? DEFAULT_WRITE_KEY);
    script.setAttribute('data-namespace', namespaceName);
    script.setAttribute('data-debug', this.options.debug ? 'true' : 'false');
    script.setAttribute('data-onload', callbackName);
    document.head.appendChild(script);
  }

  private bindEvents(): void {
    const { eventBus } = this.ctx!;

    this.unsubs.push(
      // ── User identity ──────────────────────────────────────────────────────
      eventBus.on('api:user', (user) => {
        this.userId = user.user_id;
      }),

      // ── Playback lifecycle ─────────────────────────────────────────────────
      eventBus.on('playing', () => {
        this.playbackObserved = true;
        if (!this.lag) {
          this.lastPlaybackTime = this.ctx!.store.getState().currentTime;
          this.lastPlaybackAt = Date.now();
        }
        this.trackEvent('hls_viewing_params', this.getViewingParams());
      }),
      eventBus.on('time:update', (currentTime) => {
        this.qoe?.observeProgress(currentTime);
        this.observePlaybackProgress(currentTime);
      }),
      eventBus.on('video:seeking', () => this.resetLag('seeking')),
      eventBus.on('engine:destroyed', () => {
        this.resetLag('engine_changed');
        this.recentLagError = undefined;
        this.playlistUnavailable = false;
      }),
      eventBus.on('hls:playlist:loaded', () => { this.playlistUnavailable = false; }),
      this.ctx!.store.subscribe(s => s.isPlaying && s.isOnline, active => {
        if (!active) this.resetLag(this.ctx!.store.getState().isOnline ? 'paused' : 'offline');
      }),
      eventBus.on('stopped', () => {
        this.resetLag('stopped');
        this.trackEvent('playback_stopped', {});
      }),

      // ── Fatal player error (emitted by engine for all fatal errors) ─────────
      // Only report fatal network errors when stream is actually online to avoid
      // spamming when the broadcaster simply went offline.
      eventBus.on('error', (err: PlayerError) => {
        if (err.fatal && this.isStreamOnline()) {
          this.trackEvent('hls_fatal_error', {
            code: err.code,
            message: err.message,
            fatal: err.fatal,
          });
        }
      }),

      // ── Categorised HLS errors (non-fatal + fatal pre-classified) ──────────
      eventBus.on('hls:error', (data) => {
        // A missing live playlist is availability evidence, not a playback lag.
        // Non-fatal retries can precede the engine's final offline transition.
        if (data.type === 'networkError' && data.responseCode === 404 &&
            /^(manifest|level|audioTrack|subtitleTrack)LoadError$/.test(data.details)) {
          this.playlistUnavailable = true;
          this.resetLag('offline');
          this.recentLagError = undefined;
          return;
        }
        if (this.isStreamOnline()) {
          this.recentLagError = { at: Date.now(), type: data.type, details: data.details, responseCode: data.responseCode, fatal: data.fatal };
          if (this.lag) {
            const previous = this.lag.error;
            this.lag.error = this.recentLagError;
            if (this.lag.reported && (previous?.details !== data.details ||
                previous?.type !== data.type || previous?.responseCode !== data.responseCode || previous?.fatal !== data.fatal)) {
              this.trackEvent('hls_playback_lag_update', { ...this.lagPayload(this.lag), phase: 'updated', isLag: true, recovered: false, updateKind: 'error' });
            }
          }
        }
        switch (data.type) {
          case 'networkError':
            // Only log network errors if the stream is still online —
            // avoids spam when broadcaster goes offline.
            if (this.isStreamOnline()) {
              this.trackEvent(
                data.fatal ? 'hls_fatal_error' : 'hls_network_error',
                { type: data.type, fatal: data.fatal, details: data.details, src: data.src, quality: data.quality, responseCode: data.responseCode },
              );
            }
            break;
          case 'mediaError':
            this.trackEvent('hls_media_error', {
              type: data.type, fatal: data.fatal, details: data.details, src: data.src, quality: data.quality,
            });
            break;
          default:
            // otherError and future types
            if (this.isStreamOnline()) {
              this.trackEvent('hls_other_error', {
                type: data.type, fatal: data.fatal, details: data.details, src: data.src, quality: data.quality,
              });
            }
        }
      }),

      // ── Buffer stall — only after 30 s of active playback ─────────────────
      eventBus.on('buffer:stall', (data) => {
        this.beginLag('buffer_stall', data);
        const state = this.ctx?.store.getState();
        if (this.isStreamOnline() && state?.isPlaying && data.currentTime > 30) {
          this.trackEvent('hls_buffer_stalled_error', {
            diagnosticOnly: true,
            bufferLength: data.bufferLength,
            currentTime: data.currentTime,
            src: data.src,
            quality: data.quality,
          });
        }
      }),

      // ── Buffer low — throttled ─────────────────────────────────────────────
      eventBus.on('buffer:low', (data) => {
        if (!this.isStreamOnline()) return;
        const now = Date.now();
        if (now - this.lastBufferLowAt >= BUFFER_LOW_THROTTLE) {
          this.lastBufferLowAt = now;
          this.trackEvent('hls_buffer_low', {
            bufferLength: data.bufferLength,
            bandwidth: data.bandwidth,
            bandwidthMbps: data.bandwidth / 1000,
            targetDuration: data.targetDuration,
            timeToLoad: data.timeToLoad,
            src: data.src,
            quality: data.quality,
          });
        }
      }),

      // ── Buffer critical — throttled ────────────────────────────────────────
      eventBus.on('buffer:critical', (data) => {
        if (!this.isStreamOnline()) return;
        const now = Date.now();
        if (now - this.lastBufferCriticalAt >= BUFFER_CRITICAL_THROTTLE) {
          this.lastBufferCriticalAt = now;
          this.trackEvent('hls_buffer_critical', {
            bufferLength: data.bufferLength,
            currentTime: data.currentTime,
            src: data.src,
            quality: data.quality,
          });
        }
      }),

      // ── SourceBuffer append error ──────────────────────────────────────────
      eventBus.on('buffer:append:error', (data) => {
        const now = Date.now();
        if (now - this.lastBufferAppendErrorAt >= BUFFER_APPEND_ERROR_THROTTLE) {
          this.lastBufferAppendErrorAt = now;
          this.trackEvent('hls_buffer_append_error', {
            start: data.start,
            end: data.end,
          });
        }
      }),

      // ── Video element waiting / stalled ────────────────────────────────────
      eventBus.on('video:waiting', (metrics: VideoStateMetrics) => {
        if (!this.isStreamOnline()) return;
        if (!metrics.paused && !metrics.seeking) this.beginLag('video_waiting', this.flattenVideoMetrics(metrics));
        this.trackEvent('hls_video_waiting', { ...this.flattenVideoMetrics(metrics), diagnosticOnly: true });
      }),
      eventBus.on('stream:online', () => {
        this.playlistUnavailable = false;
        this.trackEvent('stream_online', { isOnline: true });
      }),
      eventBus.on('stream:offline', () => {
        this.resetLag('offline');
        this.recentLagError = undefined;
        this.trackEvent('stream_offline', { isOnline: false });
      }),
      eventBus.on('video:stalled', (metrics: VideoStateMetrics) => {
        if (!this.isStreamOnline()) return;
        // Browser stalled describes loading. With playable data it is not a lag.
        if (metrics.readyState < 3 && !metrics.paused && !metrics.seeking) {
          this.beginLag('video_stalled', this.flattenVideoMetrics(metrics));
        }
        this.trackEvent('hls_video_stalled', { ...this.flattenVideoMetrics(metrics), diagnosticOnly: true });
      }),

      // ── Fragment sample (2-min throttle applied by engine) ────────────────
      eventBus.on('hls:frag:sample', (data) => {
        this.trackEvent('hls_frag_loaded', {
          sn: data.sn,
          level: data.level,
          duration: data.duration,
          size: data.size,
          loadTime: data.loadTime,
          bandwidth: data.bandwidth,
          bandwidthMbps: data.bandwidth / 1000,
          src: data.src,
          quality: data.quality,
        });
      }),

      // ── Aggregate speed report (10-min throttle applied by engine) ─────────
      eventBus.on('hls:speed:report', (data) => {
        if (this.isStreamOnline()) {
          this.trackEvent('hls_average_speed_loading', {
            averageSpeed: data.averageSpeed,
            overallSpeed: data.overallSpeed,
            totalBytes: data.totalBytes,
            measurements: data.measurements,
            src: data.src,
            quality: data.quality,
          });
        }
      }),
    );
  }

  /** Periodic latency gauge — reports every 2 min for LL-HLS */
  private startLatencyGauge(): void {
    this.stopLatencyGauge();
    this.latencyTimer = setInterval(() => {
      const state = this.ctx?.store.getState();
      if (!state?.isPlaying || !state?.isLowLatency) return;

      const engine = this.ctx?.getEngine();
      if (!engine) return;

      this.trackEvent('hls_latency_gauge', {
        latency: engine.getLatency(),
        bufferLength: engine.getBufferLength(),
        bandwidth: engine.getBandwidth(),
        bandwidthMbps: engine.getBandwidth(),
        quality: state.quality,
        engine: state.engineName,
      });
    }, LATENCY_GAUGE_INTERVAL);
  }

  private stopLatencyGauge(): void {
    if (this.latencyTimer) {
      clearInterval(this.latencyTimer);
      this.latencyTimer = null;
    }
  }

  /** Returns true when the stream is marked online in the store (anti-spam guard) */
  private isStreamOnline(): boolean {
    return !this.playlistUnavailable && (this.ctx?.store.getState().isOnline ?? true);
  }

  private resetLag(outcome: string = 'cancelled'): void {
    this.finishLag(outcome);
    this.recentLagError = undefined;
    this.playbackObserved = false;
    this.playbackAdvanced = false;
    this.lastPlaybackProgressAt = 0;
  }

  private beginLag(signal: string, metrics: Record<string, unknown>): void {
    const state = this.ctx?.store.getState();
    if (!state?.isPlaying || state.isSwitching || !this.isStreamOnline() || !this.playbackAdvanced) return;
    if (this.lag) {
      this.lag.signals.add(signal);
      return;
    }
    const now = Date.now();
    const error = this.recentLagError && now - this.recentLagError.at <= ERROR_CONTEXT_WINDOW_MS
      ? this.recentLagError : undefined;
    this.lag = {
      id: `${this.sessionId}_${++this.lagSequence}`,
      reported: false,
      startedAt: now,
      observedDurationMs: 0,
      unobservedDurationMs: 0,
      currentTime: typeof metrics.currentTime === 'number' ? metrics.currentTime : this.lastPlaybackTime,
      signals: new Set([signal]),
      error,
      context: {
        lastPlaybackProgressAt: this.lastPlaybackProgressAt || undefined,
        videoSnapshotAt: now,
        ...this.getViewingParams(),
        serverName: state.cdnServer,
        server: state.cdnServerIp,
        serverSource: state.cdnServerSource,
        ...metrics,
      },
    };
    const episode = this.lag;
    this.lagTimer = setTimeout(() => {
      this.lagTimer = null;
      if (this.lag !== episode) return;
      if (!this.ctx?.store.getState().isPlaying || !this.isStreamOnline()) {
        this.resetLag(this.isStreamOnline() ? 'paused' : 'offline');
        return;
      }
      if (this.ctx.store.getState().isSwitching) {
        this.resetLag('engine_changed');
        return;
      }
      const video = this.ctx.getEngine()?.getVideoElement?.();
      if (video?.paused || video?.seeking) {
        this.resetLag(video.seeking ? 'seeking' : 'paused');
        return;
      }
      if (Date.now() - episode.startedAt > MAX_LAG_OBSERVATION_GAP_MS) {
        // The threshold callback can run hours after sleep. Confirm a fresh
        // interval after waking instead of reporting the suspension as a lag.
        this.finishLag('cancelled');
        this.lastPlaybackTime = video?.currentTime ?? this.lastPlaybackTime;
        this.lastPlaybackAt = Date.now();
        this.beginLag(signal, video
          ? this.flattenVideoMetrics(buildVideoStateMetrics(video,
            signal === 'video_stalled' ? 'stalled' : 'waiting',
            typeof metrics.src === 'string' ? metrics.src : undefined,
            this.ctx.store.getState().quality))
          : { ...metrics, currentTime: this.lastPlaybackTime });
        return;
      }
      // Media may resume before the queued timeupdate handler is dispatched.
      if (video) this.observePlaybackProgress(video.currentTime);
      if (this.lag !== episode) return;
      episode.reported = true;
      this.trackEvent('hls_playback_lag', {
        ...this.lagPayload(episode), phase: 'started', isLag: true, recovered: false,
      });
    }, MIN_LAG_DURATION_MS);
  }

  private observePlaybackProgress(currentTime: number): void {
    const state = this.ctx?.store.getState();
    if (!state?.isPlaying || !this.isStreamOnline()) {
      this.resetLag(this.isStreamOnline() ? 'paused' : 'offline');
      return;
    }
    if (!Number.isFinite(currentTime)) return;
    if (!this.playbackObserved) {
      this.playbackObserved = true;
      this.lastPlaybackTime = currentTime;
      this.lastPlaybackAt = Date.now();
      return;
    }
    const previousTime = this.lastPlaybackTime;
    const now = Date.now();
    const elapsed = Math.max(0, now - this.lastPlaybackAt) / 1000;
    this.lastPlaybackTime = currentTime;
    this.lastPlaybackAt = now;
    if (currentTime <= previousTime + 0.05) return;
    const video = this.ctx?.getEngine()?.getVideoElement?.();
    if (elapsed * 1000 > MAX_LAG_OBSERVATION_GAP_MS || state.loading || state.isSwitching || video?.seeking || video?.paused ||
      currentTime - previousTime > elapsed * Math.max(1, video?.playbackRate ?? 1) + 0.5) return;
    // Only plausible progress arms lag detection. A seek/alignment jump alone
    // is not playback, including during the first startup.
    this.playbackAdvanced = true;
    this.lastPlaybackProgressAt = now;
    // Pipeline reloads can rebase the timeline below the lag's initial time.
    // Recovery depends on successive samples, not on reaching that old time.
    if (this.lag) this.finishLag('recovered', currentTime);
  }

  private finishLag(outcome: string, currentTime?: number): void {
    if (this.lagTimer) clearTimeout(this.lagTimer);
    this.lagTimer = null;
    const episode = this.lag;
    this.lag = null;
    if (!episode?.reported) return;
    this.trackEvent('hls_playback_lag_end', {
      ...this.lagPayload(episode),
      phase: 'ended',
      outcome,
      isLag: outcome !== 'offline',
      recovered: outcome === 'recovered',
      recoveredCurrentTime: outcome === 'recovered' ? currentTime : undefined,
    });
  }

  private lagPayload(episode: LagEpisode): Record<string, unknown> {
    const error = episode.error;
    const buffer = episode.context.videoBufferLength ?? episode.context.bufferTime ?? episode.context.bufferLength;
    const reason = error?.type === 'networkError'
      ? (/timeout/i.test(error.details) ? 'network_timeout' : error.responseCode ? 'network_http_error' : 'network_error')
      : error?.type === 'mediaError' ? 'media_error'
      : typeof buffer === 'number' && buffer < 0.5 ? 'buffer_empty' : 'unknown';
    return {
      ...episode.context,
      lagId: episode.id,
      startedAt: episode.startedAt,
      durationMs: Date.now() - episode.startedAt,
      observedDurationMs: episode.observedDurationMs,
      unobservedDurationMs: episode.unobservedDurationMs,
      reason,
      reasonEvidence: error ? 'correlated_hls_error' : reason === 'buffer_empty' ? 'buffer_observation' : 'unknown',
      errorType: error?.type,
      errorDetails: error?.details,
      responseCode: error?.responseCode,
      errorFatal: error?.fatal,
      errorOffsetMs: error ? error.at - episode.startedAt : undefined,
      signals: [...episode.signals],
    };
  }

  private trackEvent(eventName: string, data: Record<string, unknown>): void {
    const j = this.jitsu ?? (window as unknown as Record<string, unknown>)['playerJitsu'] ?? null;

    const state = this.ctx?.store.getState();
    const userId = this.getUserId();
    const channelId = Number(this.ctx?.config.apiData?.channel_id ?? state?.streamKey);
    const isPlayerMetric = eventName.startsWith('hls_') || eventName.startsWith('ws_') ||
      eventName === 'stream_online' || eventName === 'stream_offline' || eventName === 'playback_stopped';
    const metrics = isPlayerMetric ? {
      telemetryVersion: 3,
      qoeRevision: 4,
      sessionId: this.sessionId,
      sequence: ++this.sequence,
      eventAt: Date.now(),
      playerVersion,
      playerCommit: typeof __GGPLAYER_COMMIT_HASH__ === 'string' ? __GGPLAYER_COMMIT_HASH__ : 'unknown',
      ...this.getClientInfo(),
      visibility: document.visibilityState === 'hidden' ? 'hidden' : 'visible',
      isOnline: state?.isOnline,
      isPlaying: state?.isPlaying,
      engine: state?.engineName,
      serverName: state?.cdnServer ?? undefined,
      server: state?.cdnServerIp ?? undefined,
      serverSource: state?.cdnServerSource ?? undefined,
      ch: Number.isFinite(channelId) && channelId > 0 ? channelId : undefined,
      ...data,
    } : data;

    const payload = {
        data: metrics,
        userId,
        streamKey: state?.streamKey,
    };
    if (!j) {
      if (this.pendingEvents.length >= 200) this.pendingEvents.shift();
      this.pendingEvents.push({ name: eventName, payload });
      return;
    }
    this.flushPendingEvents(j);
    try {
      (j as { track: (name: string, payload: unknown) => void }).track(eventName, payload);
    } catch (e) {
      this.ctx?.logger.warn('Analytics track error:', e);
    }
  }

  private getUserId(): string | number {
    return this.userId ?? this.anonymousId;
  }

  private getViewingParams(): Record<string, unknown> {
    const state = this.ctx?.store.getState();
    const engine = this.ctx?.getEngine();
    return {
      quality: state?.quality,
      volume: state?.volume,
      latency: engine?.getLatency() ?? 0,
      bandwidth: engine?.getBandwidth() ?? 0,
      bandwidthMbps: engine?.getBandwidth() ?? 0,
      bufferLength: engine?.getBufferLength() ?? 0,
      engine: state?.engineName,
    };
  }

  private flushPendingEvents(j: unknown): void {
    const pending = this.pendingEvents;
    this.pendingEvents = [];
    for (const event of pending) {
      try { (j as { track: (name: string, payload: unknown) => void }).track(event.name, event.payload); }
      catch (e) { this.ctx?.logger.warn('Analytics track error:', e); }
    }
  }

  private getClientInfo(): Record<string, string> {
    const ua = navigator.userAgent;
    const browser = /Edg\//.test(ua) ? 'edge' : /Firefox\//.test(ua) ? 'firefox'
      : /Chrome\//.test(ua) ? 'chrome' : /Safari\//.test(ua) ? 'safari' : 'other';
    const os = /Android/.test(ua) ? 'android' : /iPhone|iPad|iPod/.test(ua) ? 'ios'
      : /Windows/.test(ua) ? 'windows' : /Macintosh/.test(ua) ? 'macos' : /Linux/.test(ua) ? 'linux' : 'other';
    return { browser, os, deviceType: /iPad|Tablet/.test(ua) ? 'tablet' : /Mobile|iPhone|Android/.test(ua) ? 'mobile' : 'desktop' };
  }

  private flattenVideoMetrics(metrics: VideoStateMetrics): Record<string, unknown> {
    return {
      currentTime: metrics.currentTime,
      paused: metrics.paused,
      seeking: metrics.seeking,
      videoBufferLength: metrics.bufferLength,
      src: metrics.src,
      quality: metrics.quality,
      duration: metrics.duration,
      networkState: metrics.networkState,
      networkStateDescription: metrics.networkStateDescription,
      readyState: metrics.readyState,
      readyStateDescription: metrics.readyStateDescription,
      bufferedRangesCount: metrics.bufferedRanges.length,
      bufferTime: metrics.bufferTime,
    };
  }

  private getOrCreateAnonymousId(): string {
    const KEY = 'GG_ANONYMOUS_USER_ID';
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = `0_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`;
      localStorage.setItem(KEY, id);
    }
    return id;
  }
}
